class KitSelector {
  constructor(element) {
    this.section = element;
    this.forms = this.section.querySelectorAll('.kit-form');
    this.modal = document.getElementById('kit-confirmation-modal');
    this.partnerPromptModal = this.section.querySelector('#kit-partner-prompt-modal');

    this.descriptions = this.section.querySelectorAll('[data-description]');
    this.readMoreButtons = this.section.querySelectorAll('[data-read-more]');
    
    this.carousel = this.section.querySelector('[data-carousel]');
    this.initCarousel();
    
    this.initReadMore();
    
    // Add debounced resize handler
    this.debouncedResize = this.debounce(() => this.handleResize(), 250);
    window.addEventListener('resize', this.debouncedResize);

    this.initInfoCardsCarousel();

    // Listen to partner selection
    document.addEventListener("theme:partner:set", (event) => {
      this.partner = event.detail.partner;
      this.initKitSelection();
    });

    // Add modal handling
    this.selectedVariantId = null;
    this.selectedSellingPlanId = null;
    this.initKitSelection();

    // Add tracking for selected add-ons
    this.selectedAddons = new Set();
    this.initAddonSelection();

    // Initialize continue button handler
    this.initContinueButton();

    // Store original button texts
    this.kitButtons = this.section.querySelectorAll('[data-select-kit]');
    this.kitButtons.forEach(button => {
      button.dataset.originalText = button.textContent;
    });

    window.addEventListener('pageshow', (event) => {
      setTimeout(() => {
        this.checkCartAndInitialize();
      }, 100);
    });

    // Get and log customer data
    this.initCustomerData();

    // Check if customer is a consultant and disable buttons if true
    if (this.isConsultant()) {
      this.disableAllKitButtons();
    }
  }

  disableAllKitButtons() {
    this.kitButtons.forEach(button => {
      button.disabled = true;
      button.classList.add('disabled');
      button.textContent = window.theme.strings.enrollmentNotAllowed;
      button.closest('.kit-card').classList.add('kit-card--disabled');
    });
  }

  initCustomerData() {
    try {
      const customerDataElement = this.section.getAttribute('data-customer');
      if (customerDataElement && customerDataElement !== 'null') {
        // Decode HTML entities and parse JSON
        const decodedData = customerDataElement
          .replace(/&quot;/g, '"')
          .replace(/&#39;/g, "'")
          .replace(/&lt;/g, '<')
          .replace(/&gt;/g, '>')
          .replace(/&amp;/g, '&');
        
        const customer = JSON.parse(decodedData);
        this.customer = customer;
      } else {
        console.log('Customer not logged in');
        this.customer = null;
      }
    } catch (error) {
      console.error('Error parsing customer data:', error);
      console.log('Raw customer data:', this.section.getAttribute('data-customer'));
      this.customer = null;
    }
  }

  checkCartAndInitialize() {
    this.checkCart().then(() => {
      this.forms.forEach(form => {
        form.addEventListener('submit', (e) => {
          e.preventDefault();
          if (form.closest('.kit-card').classList.contains('kit-card--selected')) {
            this.unselectKit(form);
          } else {
            if (this.modal) {
              this.showConfirmationModal(form);
            } else {
              this.handleKitSelection(form);
            }
          }
        });
      });
    });
  }

  initReadMore() {
    // Only initialize read more on mobile
    if (window.innerWidth > 749) {
      this.descriptions.forEach(description => {
        description.style.maxHeight = 'none';
      });
      this.readMoreButtons.forEach(button => {
        button.style.display = 'none';
      });
      return;
    }

    this.readMoreButtons.forEach((button, index) => {
      const description = this.descriptions[index];
      const lineHeight = parseInt(window.getComputedStyle(description).lineHeight);
      const maxLines = 6;
      const maxHeight = lineHeight * maxLines;
      
      if (description.scrollHeight > maxHeight) {
        button.style.display = 'inline-block';
        description.style.maxHeight = `${maxHeight}px`;
      } else {
        button.style.display = 'none';
        description.style.maxHeight = 'none';
      }

      button.addEventListener('click', () => {
        description.classList.add('expanded');
        description.style.maxHeight = 'none';
        button.style.display = 'none';
        
        if (this.carousel) {
          setTimeout(() => {
            const cards = this.carousel.querySelectorAll('.kit-card');
            let maxHeight = 0;

            cards.forEach(card => {
              card.style.height = 'auto';
            });

            cards.forEach(card => {
              const height = card.offsetHeight;
              maxHeight = Math.max(maxHeight, height);
            });

            cards.forEach(card => {
              card.style.height = `${maxHeight}px`;
            });

            const viewport = this.carousel.querySelector('.flickity-viewport');
            if (viewport) {
              viewport.style.height = `${maxHeight}px`;
            }
          }, 300);
        }
      });
    });
  }

  handleResize() {
    // Reset descriptions on desktop
    if (window.innerWidth > 749) {
      this.descriptions.forEach(description => {
        description.style.maxHeight = 'none';
        description.classList.remove('expanded');
      });
      this.readMoreButtons.forEach(button => {
        button.style.display = 'none';
      });
    } else {
      // Mobile behavior
      this.readMoreButtons.forEach((button, index) => {
        const description = this.descriptions[index];
        const lineHeight = parseInt(window.getComputedStyle(description).lineHeight);
        const maxLines = 6;
        const maxHeight = lineHeight * maxLines;
        
        description.classList.remove('expanded');
        
        if (description.scrollHeight > maxHeight) {
          button.style.display = 'inline-block';
          description.style.maxHeight = `${maxHeight}px`;
        } else {
          button.style.display = 'none';
          description.style.maxHeight = 'none';
        }
      });
    }

    // Always handle card heights
    if (this.carousel) {
      const cards = this.carousel.querySelectorAll('.kit-card');
      let maxHeight = 0;

      cards.forEach(card => {
        card.style.height = 'auto';
      });

      cards.forEach(card => {
        const height = card.offsetHeight;
        maxHeight = Math.max(maxHeight, height);
      });

      cards.forEach(card => {
        card.style.height = `${maxHeight}px`;
      });

      const viewport = this.carousel.querySelector('.flickity-viewport');
      if (viewport) {
        viewport.style.height = `${maxHeight}px`;
      }
    }

    // Handle info cards carousel
    const infoCardsCarousel = document.querySelector('.info-cards [data-carousel]');
    if (infoCardsCarousel) {
      const cards = infoCardsCarousel.querySelectorAll('.info-card');
      let maxHeight = 0;

      cards.forEach(card => {
        card.style.height = 'auto';
      });

      cards.forEach(card => {
        const height = card.offsetHeight;
        maxHeight = Math.max(maxHeight, height);
      });

      cards.forEach(card => {
        card.style.height = `${maxHeight}px`;
      });

      const viewport = infoCardsCarousel.querySelector('.flickity-viewport');
      if (viewport) {
        viewport.style.height = `${maxHeight}px`;
      }
    }
  }

  initCarousel() {
    if (this.carousel) {
      setTimeout(() => {
        const cards = this.carousel.querySelectorAll('.kit-card');
        let maxHeight = 0;

        // Find the tallest card
        cards.forEach(card => {
          const height = card.offsetHeight;
          maxHeight = Math.max(maxHeight, height);
        });

        // Set all cards to the tallest height
        cards.forEach(card => {
          card.style.height = `${maxHeight}px`;
        });

        // Update viewport height
        const viewport = this.carousel.querySelector('.flickity-viewport');
        if (viewport) {
          viewport.style.height = `${maxHeight}px`;
        }
      }, 100); // Small delay to ensure content is rendered
    }
  }

  initInfoCardsCarousel() {
    const infoCardsCarousel = document.querySelector('.info-cards [data-carousel]');
    if (infoCardsCarousel) {
      setTimeout(() => {
        const cards = infoCardsCarousel.querySelectorAll('.info-card');
        let maxHeight = 0;

        // Reset heights to auto first
        cards.forEach(card => {
          card.style.height = 'auto';
        });

        // Find the tallest card
        cards.forEach(card => {
          const height = card.offsetHeight;
          maxHeight = Math.max(maxHeight, height);
        });

        // Set all cards to the tallest height
        cards.forEach(card => {
          card.style.height = `${maxHeight}px`;
        });

        // Update viewport height
        const viewport = infoCardsCarousel.querySelector('.flickity-viewport');
        if (viewport) {
          viewport.style.height = `${maxHeight}px`;
        }
      }, 100);
    }
  }

  // Add debounce helper method
  debounce(func, wait) {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), wait);
    };
  }

  // Clean up method to remove event listeners
  destroy() {
    window.removeEventListener('resize', this.debouncedResize);
  }

  initKitSelection() {
    const kitButtons = this.section.querySelectorAll('[data-select-kit]');
    kitButtons.forEach(button => {
      button.addEventListener('click', async (e) => {
        e.preventDefault();
        
        // Check if this is the selected kit
        const isSelected = button.closest('.kit-card').classList.contains('kit-card--selected');
        
        if (isSelected) {
          // Remove kit from cart
          try {
            const response = await fetch('/cart.js');
            const cart = await response.json();
            
            // Find kit and addon items to remove
            const itemsToRemove = cart.items
              .filter(item => 
                item.product_type === "Kit" || 
                item.product_type === "Enrollment Add-On"
              )
              .map(item => item.variant_id);

            // Remove items from cart
            await fetch('/cart/update.js', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                updates: itemsToRemove.reduce((acc, id) => ({
                  ...acc,
                  [id]: 0
                }), {})
              })
            });

            // Reset UI
            this.kitButtons.forEach(btn => {
              btn.disabled = false;
              btn.classList.remove('disabled');
              btn.textContent = btn.dataset.originalText;
              btn.closest('.kit-card').classList.remove('kit-card--selected', 'kit-card--disabled');
            });

            this.selectedVariantId = null;
            this.selectedSellingPlanId = null;
            this.selectedAddons.clear();

          } catch (error) {
            console.error('Error removing items from cart:', error);
          }
        } else if (!this.partner) {
          // Prompt partner select
          if (this.partnerPromptModal) {
            this.partnerPromptButton = this.partnerPromptModal.querySelector('[data-kit-prompt-partner-select-button]');
            this.partnerPromptButton.addEventListener('click', () => {
              MicroModal.close("kit-partner-prompt-modal");
              MicroModal.show("find-your-consultant", {
                disableScroll: true,
                disableFocus: true,
              });
            });

            MicroModal.show("kit-partner-prompt-modal", { 
              disableScroll: true, disableFocus: true 
            });
          }
        } else {
          // Normal flow for selecting a new kit
          this.selectedVariantId = button.dataset.variantId;
          this.selectedSellingPlanId = button.dataset.sellingPlanId !== "" ? button.dataset.sellingPlanId : null;
          this.openModal();
          this.updateTotal();
        }
      });
    });

    if (this.modal) {
      // Handle overlay clicks
      const overlay = this.modal.querySelector('.modal__overlay');
      overlay.addEventListener('click', (e) => {
        // Only close if clicking directly on the overlay
        if (e.target === overlay) {
          this.closeModal();
        }
      });

      // Handle close button clicks
      const closeButton = this.modal.querySelector('.modal__close');
      if (closeButton) {
        closeButton.addEventListener('click', () => this.closeModal());
      }
    }
  }

  openModal() {
    if (this.modal) {
      this.modal.setAttribute('aria-hidden', 'false');
      this.modal.style.display = 'block';
    }
  }

  closeModal() {
    if (this.modal) {
      this.modal.setAttribute('aria-hidden', 'true');
      this.modal.style.display = 'none';
    }
  }

  initAddonSelection() {
    if (this.modal) {
      const addonButtons = this.modal.querySelectorAll('[data-addon-select]');
      addonButtons.forEach(button => {
        button.addEventListener('click', () => {
          const variantId = button.dataset.variantId;
          const price = parseFloat(button.dataset.price);
          
          if (this.selectedAddons.has(variantId)) {
            // Deselect
            this.selectedAddons.delete(variantId);
            button.classList.remove('selected');
            button.textContent = `Select Add On - ${this.formatMoney(price)}`;
          } else {
            // Select
            this.selectedAddons.add(variantId);
            button.classList.add('selected');
            button.textContent = 'Selected';
          }
          
          this.updateTotal();
        });
      });
    }
  }

  updateTotal() {
    const totalElement = this.modal.querySelector('[data-total]');
    if (!totalElement) return;

    let total = 0;
    
    // Get selected kit price
    const kitPrice = this.getSelectedKitPrice();
    total += kitPrice;

    // Add selected add-ons prices
    this.modal.querySelectorAll('[data-addon-select]').forEach(button => {
      if (this.selectedAddons.has(button.dataset.variantId)) {
        total += parseFloat(button.dataset.price);
      }
    });

    totalElement.textContent = this.formatMoney(total);
  }

  getSelectedKitPrice() {
    const kitButton = this.section.querySelector(`[data-variant-id="${this.selectedVariantId}"]`);
    if (kitButton) {
      const priceText = kitButton.closest('.kit-card').querySelector('.kit-card__price').textContent;
      return parseFloat(priceText.replace(/[^0-9.]/g, '')) * 100; // Convert to cents
    }
    return 0;
  }

  formatMoney(cents) {
    return `$${(cents/100).toFixed(2)}`;
  }

  initContinueButton() {
    if (this.modal) {
      const continueButton = this.modal.querySelector('[data-confirm-kit]');
      if (continueButton) {
        continueButton.addEventListener('click', () => this.addToCart());
      }
    }
  }

  async addToCart() {
    const continueButton = this.modal.querySelector('[data-confirm-kit]');
    const originalText = continueButton.textContent;
    
    try {
      // Disable button and show loading state
      continueButton.disabled = true;
      continueButton.textContent = 'Adding to cart...';

      // Prepare items array with selected kit and add-ons
      const items = [
        {
          id: this.selectedVariantId,
          quantity: 1,
          selling_plan: this.selectedSellingPlanId
        }
      ];

      // Add selected add-ons to items array
      this.selectedAddons.forEach(variantId => {
        items.push({
          id: variantId,
          quantity: 1
        });
      });

      // Add items to cart
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          items: items
        })
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      let sessionId = sessionStorage.getItem("CHECKOUT_CUSTOM_SESSION_ID");
      if (!sessionId) {
        sessionId = Date.now();
        sessionStorage.setItem("CHECKOUT_CUSTOM_SESSION_ID", sessionId);
      }

      const context = {
        curSite: localStorage.getItem("CURRENT_PARTNER_STORAGE_KEY") ?? null,
        customerId: window?.customer?.id,
        customerEmail: window?.customer?.email,
        isPartner: window?.customer?.isPartner,
        taxExempt: window.customer ? window.customer.taxExempt === "true" : false,
        userType: window.customer ? window.customer.userType : null,
        sharedCoupon: sessionStorage.getItem('COUPON_CODE') ?? null,
        houseId: sessionStorage.getItem('PAID_AFFILIATION_ID') ?? null,
        distributorId: window.customer ? window.customer.distributorId : null,
      };

      const params = new URLSearchParams();
      params.append("attributes[sessionID]", sessionId);
      params.append("attributes[context]", btoa(JSON.stringify(context)));

      const checkoutURL = `/checkout?${params.toString()}`;
      console.log("Redirecting to checkout:", checkoutURL);

      window.location.href = checkoutURL;
    } catch (error) {
      console.error(error);
    }
  }

  async checkCart() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      const kitVariantIds = Array.from(this.forms).map(form => 
        form.querySelector('[name="id"]').value
      );

      const kitInCart = cart.items.find(item => 
        kitVariantIds.includes(item.variant_id.toString())
      );

      if (kitInCart) {
        const selectedForm = Array.from(this.forms).find(form => 
          form.querySelector('[name="id"]').value === kitInCart.variant_id.toString()
        );

        if (selectedForm) {
          const button = selectedForm.querySelector('.kit-card__button');
          button.removeAttribute('disabled');
          button.classList.remove('disabled');
          button.textContent = 'Unselect Kit';
          selectedForm.closest('.kit-card').classList.remove('kit-card--disabled');
          selectedForm.closest('.kit-card').classList.add('kit-card--selected');
          this.selectedKit = kitInCart.variant_id.toString();

          this.forms.forEach(form => {
            if (form !== selectedForm) {
              const otherButton = form.querySelector('.kit-card__button');
              otherButton.disabled = true;
              form.closest('.kit-card').classList.add('kit-card--disabled');
            }
          });
        }
      }
    } catch (error) {
      console.error('Error checking cart:', error);
    }
  }

  isConsultant() {
    try {
      if (this.customer && this.customer.tags) {
        // Convert tags to array if it's a string
        const tags = Array.isArray(this.customer.tags) 
          ? this.customer.tags 
          : this.customer.tags.split(',').map(tag => tag.trim());
        
        return tags.includes('UserType_Consultant');
      }
      return false;
    } catch (error) {
      console.error('Error checking consultant status:', error);
      return false;
    }
  }

}

document.addEventListener('shopify:section:load', (event) => {
  const section = event.target;
  if (section.hasAttribute('data-kit-selector')) {
    new KitSelector(section);
  }
});

// Add resize handler if needed
window.addEventListener('resize', () => {
  const kitSections = document.querySelectorAll('[data-kit-selector]');
  if (kitSections.length) {
    kitSections.forEach(section => {
      const kitSelector = new KitSelector(section);
      kitSelector.handleResize();
    });
  }
});

// Add this to your existing JavaScript
function initInfoCards() {
  setTimeout(() => {
    const infoCards = document.querySelector('.info-cards');
    if (infoCards) {
      infoCards.classList.add('is-loaded');
    }
  }, 300);
}

// Call this function when the page loads
document.addEventListener('DOMContentLoaded', initInfoCards);